import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';
import { AccommodationDetailListItem, formatCurrency } from 'voyager';

import { useGetPackagesAvailability } from '@features/packages/hooks/useGetPackagesAvailability';
import { isDynamicPackage } from '@features/packages/utils/isDynamicPackage';
import { useRate, useRatePageSettings } from '@features/rates';
import { moreRoomsAvailableThanInBasket } from '@features/rates/helpers/moreRoomsAvailableThanInBasket.helper';
import { RateRoom } from '@features/rates/types';
import { useRoomPageSettings } from '@features/rooms';
import { TranslationsFunc, useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import rewriteHttp from '@utils/rewriteHttp';

export const mapRoomToDetailItem = (
  room: RateRoom,
  t: TranslationsFunc
): AccommodationDetailListItem => ({
  id: room.id,
  name: room.name,
  imageUrl: room.imageUrl ? rewriteHttp(room.imageUrl) : undefined,
  imageAlt: room.name,
  price: {
    amount: room.pricePerStay.amount,
    currencyCode: room.pricePerStay.currencyCode,
  },
  secondaryPriceLabel: t('room.pricePerNight', {
    pricePerNight: formatCurrency(room.pricePerNight.amount, room.pricePerNight.currencyCode),
  }),
});

export function useRateDetail(
  journeyParams: ParsedJourneyParams,
  roomIdsInBasket: string[],
  language: string,
  property: Property
) {
  const { t } = useTranslations();
  const { query } = useRouter();

  const rateCode = query.id as string;
  const promoRateCode = queryParamToString(query.promoRateCode);
  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const { rate, isFetching, isLoading, error, isAvailabilityUnknown, refetchAvailability } =
    useRate(
      rateCode,
      {
        fromDate: parseDateToString(journeyParams.fromDate),
        toDate: parseDateToString(journeyParams.toDate),
        adults,
        children,
        childAges: journeyParams.childAges,
        promoRateCode,
        rooms,
      },
      language
    );

  const { content: ratesContent } = useRatePageSettings(language, t('common.rate'));
  const { content: roomsContent } = useRoomPageSettings(language, t('common.rate'));
  const fallbackImageUrl =
    ratesContent?.heroBackgroundImage ?? roomsContent?.heroBackgroundImage ?? undefined;

  const isPackage = isDynamicPackage(rate?.package);
  const hasPackageToCheck = isPackage || rate?.package?.meta?.entityType === 'SpaPackage';
  const {
    data: packagesAvailability,
    isFetching: isFetchingPackagesAvailability,
    refetch: refetchPackagesAvailability,
  } = useGetPackagesAvailability(property, rate ? [rate] : [], journeyParams, hasPackageToCheck);
  const isPackageUnavailable = hasPackageToCheck && !!packagesAvailability?.[0]?.isUnavailable;
  const isPackageCheckFailed = hasPackageToCheck && !!packagesAvailability?.[0]?.isCheckFailed;

  const availableRooms = (rate?.rooms ?? []).filter((room) =>
    moreRoomsAvailableThanInBasket(room, roomIdsInBasket)
  );
  const items = availableRooms.map((room) => mapRoomToDetailItem(room, t));

  const retryAvailabilityCheck = () => {
    void refetchAvailability();
    if (hasPackageToCheck) void refetchPackagesAvailability();
  };

  return {
    rateCode,
    rate,
    availableRooms,
    items,
    fallbackImageUrl,
    isPackage,
    isPackageUnavailable,
    isPackageCheckFailed,
    isAvailabilityUnknown,
    retryAvailabilityCheck,
    isFetching: isFetching || isFetchingPackagesAvailability,
    isLoading,
    error,
  };
}
